import { MaterialSelection, ModelCategory } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import {
  editLayoutModelProperties,
  getChildrenFinder,
  getComponentDesignDescendants,
  getDescendants,
  modelPropertyChanged,
  saveAllProjectChanges,
} from '@paragon/layout-models';
import { enqueueError } from '@paragon/notification-tools';
import { runWithSpinner } from '@paragon/task-spinner';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { SaveAction } from '../../components/interface/subpanels/settings/SettingsSaveButton';
import { setMaterialSelection } from '../slices/componentDesignsSlice';
import { setComponentDesignTimestamps } from '../slices/componentDesignTimestampsSlice';
import { setSettingsPanelClosed } from '../slices/panelDataSlice';
import { AsyncThunkActionVoid } from './thunkActions';

export const saveMaterialSelection =
  (layoutModelGuid: string, selection: MaterialSelection, saveAction: SaveAction): AsyncThunkActionVoid =>
  async (dispatch) => {
    try {
      await dispatch(
        runWithSpinner(
          'Saving material settings...',
          dispatch(saveMaterialSelectionRaw(layoutModelGuid, selection, saveAction)),
        ),
      );
    } catch (error) {
      dispatch(enqueueError('Error saving material settings.  Please contact Paragon Customer Support.'));
      console.error(error);
      return;
    }

    dispatch(setSettingsPanelClosed());
  };

const saveMaterialSelectionRaw =
  (layoutModelGuid: string, selection: MaterialSelection, saveAction: SaveAction): AsyncThunkActionVoid =>
  async (dispatch) => {
    switch (saveAction) {
      case SaveAction.DefaultsForHierarchy: {
        await dispatch(saveMaterialSelectionForNonComponentDesignDescendants(layoutModelGuid, selection));
        return;
      }

      case SaveAction.DefaultsForSingleLevel: {
        dispatch(editLayoutModelProperties(layoutModelGuid, { materialSelection: selection }));
        await dispatch(saveAllProjectChanges());
        return;
      }

      case SaveAction.ActualForHierarchy: {
        await Promise.all([
          dispatch(saveMaterialSelectionForNonComponentDesignDescendants(layoutModelGuid, selection)),
          dispatch(saveMaterialSelectionForComponentDesignDescendants(layoutModelGuid, selection)),
        ]);
        return;
      }

      case SaveAction.ActualForSingleComponent:
        await dispatch(saveMaterialSelectionForComponentDesign(layoutModelGuid, selection));
        return;

      default:
        assertExhaustiveSwitch(saveAction, 'Unrecognized save action.');
    }
  };

const saveMaterialSelectionForNonComponentDesignDescendants =
  (layoutModelGuid: string, selection: MaterialSelection): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const nonComponentDesignDescendantGuids = getDescendants(layoutModelGuid, getChildrenFinder(getState()))
      .filter((model) => model.category !== ModelCategory.ComponentDesign)
      .map((model) => model.guid);

    const modelGuids = [layoutModelGuid].concat(nonComponentDesignDescendantGuids);

    dispatch(
      modelPropertyChanged({
        modelGuids,
        propertyName: 'materialSelection',
        newValue: selection,
      }),
    );
    await dispatch(saveAllProjectChanges());
  };

const saveMaterialSelectionForComponentDesignDescendants =
  (layoutModelGuid: string, selection: MaterialSelection): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    if (state.materialLists.state !== 'loaded') {
      dispatch(enqueueError('Material lists are not loaded. Please refresh and retry.'));
      return;
    }

    const componentDesignDescendants = getComponentDesignDescendants(layoutModelGuid, getChildrenFinder(state));
    for (const componentDesign of componentDesignDescendants) {
      dispatch(
        setMaterialSelection({
          componentDesignGuid: componentDesign.guid,
          materialSelection: selection,
        }),
      );
    }
  };

const saveMaterialSelectionForComponentDesign =
  (componentDesignGuid: string, materialSelection: MaterialSelection): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    if (state.materialLists.state !== 'loaded') {
      dispatch(enqueueError('Material lists are not loaded. Please refresh and retry.'));
      return;
    }
    const timestamps = await designService.componentDesigns.setMaterialSelection(
      componentDesignGuid,
      materialSelection,
    );

    dispatch(setComponentDesignTimestamps({ timestamps }));
    dispatch(
      setMaterialSelection({
        componentDesignGuid,
        materialSelection,
      }),
    );
  };
